import { useCallback, useMemo, useState } from "react";

interface UsePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface UsePaginationReturn {
  currentPage: number;
  pageSize: number;

  zeroBasedPage: number; // For API calls (0-indexed)

  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  resetPage: () => void;

  calculateTotalPages: (totalItems: number) => number;
}

/**
 * Hook for managing pagination state.
 *
 * - `currentPage`: 1-based page number for display
 * - `zeroBasedPage`: 0-based page number for API calls
 * - Automatically resets to page 1 when page size changes
 * - Provides callbacks for page and page size changes
 *
 * @example
 * const {
 *   currentPage,
 *   zeroBasedPage,
 *   pageSize,
 *   handlePageChange,
 *   handlePageSizeChange,
 *   resetPage,
 *   calculateTotalPages
 * } = usePagination({ initialPageSize: 25 })
 *
 * // Use zeroBasedPage for API queries
 * const { data } = useQuery({ page: zeroBasedPage, size: pageSize })
 *
 * // Calculate total pages
 * const totalPages = calculateTotalPages(data?.totalElements || 0)
 *
 * // Pass to DataTablePagination
 * <DataTablePagination
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPageChange={handlePageChange}
 *   pageSize={pageSize}
 *   onPageSizeChange={handlePageSizeChange}
 * />
 */
export function usePagination(
  options: UsePaginationOptions = {}
): UsePaginationReturn {
  const {
    initialPage = 1,
    initialPageSize = 25,
    onPageChange,
    onPageSizeChange,
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const zeroBasedPage = useMemo(() => currentPage - 1, [currentPage]);

  const setPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      onPageChange?.(page);
    },
    [onPageChange]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
      setCurrentPage(1);
      onPageSizeChange?.(size);
    },
    [onPageSizeChange]
  );

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const calculateTotalPages = useCallback(
    (totalItems: number) => {
      return Math.ceil(totalItems / pageSize) || 1;
    },
    [pageSize]
  );

  return {
    currentPage,
    pageSize,
    zeroBasedPage,
    setPage,
    setPageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPage,
    calculateTotalPages,
  };
}

export type { UsePaginationOptions, UsePaginationReturn };
